import React from "react";
import { Link } from "react-router-dom";
import "../styles/PrivacyPolicy.css";

const Section = ({ id, title, children }) => (
  <section id={id} className="pp-section">
    {title && <h2 className="pp-h2">{title}</h2>}
    {children}
  </section>
);

const TOC_ITEMS = [
  "Acceptance of Terms",
  "What OGM Is (and Isn't)",
  "User Accounts",
  "Listings & Content Accuracy",
  "Broker & Developer Registration",
  "AI Property Advisor",
  "Prohibited Conduct",
  "Intellectual Property",
  "Third-Party Services",
  "Limitation of Liability",
  "Termination",
  "Governing Law",
  "Changes to These Terms",
  "Contact Us",
];

/**
 * Static Terms & Conditions page, matching PrivacyPolicy's layout
 * (hero, table of contents, numbered sections, contact block).
 */
export default function Terms() {
  return (
    <div className="pp-page">
      <div className="pp-container">

        <div className="pp-hero">
          <div className="pp-hero-badge">Legal</div>
          <h1 className="pp-h1">Terms &amp; Conditions</h1>
          <p className="pp-last-updated">Last updated: July 31, 2026</p>
          <p className="pp-intro">
            These Terms &amp; Conditions govern Your use of One Global Marketplace
            (the "Service", "OGM", "We", "Us"). By accessing or using the Service,
            You agree to be bound by these Terms. If You do not agree, please do
            not use the Service.
          </p>
        </div>

        <nav className="pp-toc">
          <div className="pp-toc-title">Contents</div>
          {TOC_ITEMS.map((item, i) => (
            <a key={i} href={`#term-section-${i}`} className="pp-toc-link">
              <span className="pp-toc-num">{String(i + 1).padStart(2, "0")}</span>
              {item}
            </a>
          ))}
        </nav>

        <Section id="term-section-0" title="Acceptance of Terms">
          <p>
            By creating an account, browsing listings, registering as a broker or
            developer, or otherwise using the Service, You confirm that You are
            at least 18 years old and legally capable of entering into a binding
            agreement, and You accept these Terms in full.
          </p>
        </Section>

        <Section id="term-section-1" title="What OGM Is (and Isn't)">
          <p>
            One Global Marketplace is a listings and discovery platform that
            connects property buyers/renters with sellers, developers, and
            registered brokers. OGM is <strong>not</strong> a real estate broker,
            agent, or party to any transaction between users. We do not own,
            manage, or sell any property listed on the Service, and We do not
            take a commission on any deal reached between users.
          </p>
          <p>
            RERA registrations, title verification, and legal compliance for any
            listed property remain the sole responsibility of the seller,
            developer, or broker who posted it — not OGM.
          </p>
        </Section>

        <Section id="term-section-2" title="User Accounts">
          <p>
            You are responsible for maintaining the confidentiality of Your
            account credentials and for all activity that occurs under Your
            account. Notify Us immediately at{" "}
            <a href="mailto:info@oneglobalmarketplace.com">info@oneglobalmarketplace.com</a>{" "}
            if You suspect unauthorized use of Your account.
          </p>
        </Section>

        <Section id="term-section-3" title="Listings & Content Accuracy">
          <p>
            All property details — price, area, amenities, RERA status, images,
            and availability — are provided directly by the seller, developer,
            or broker who created the listing. OGM does not independently verify
            this information. Always confirm details directly with the lister
            and conduct Your own due diligence, including a site visit and legal
            verification, before making any payment or commitment.
          </p>
        </Section>

        <Section id="term-section-4" title="Broker & Developer Registration">
          <p>
            Brokers and developers may register to list properties via the
            Service, including through phone/email verification, WhatsApp, or
            Google sign-in. Registered brokers are responsible for the accuracy
            of every listing they publish and for complying with applicable
            real estate regulations, including RERA where required.
          </p>
        </Section>

        <Section id="term-section-5" title="AI Property Advisor">
          <p>
            The Service includes an AI-powered property search and chat
            assistant. Responses are generated automatically and are provided
            for general guidance only — they do not constitute legal, financial,
            or investment advice, and may occasionally be inaccurate or
            incomplete. Always verify property details, pricing, and legal
            status independently before making any decision.
          </p>
        </Section>

        <Section id="term-section-6" title="Prohibited Conduct">
          <ul className="pp-list">
            <li>Posting false, misleading, or fraudulent listings or information</li>
            <li>Impersonating another person, broker, or developer</li>
            <li>Attempting to circumvent security, rate limits, or authentication</li>
            <li>Scraping, copying, or redistributing listing data without permission</li>
            <li>Using the Service for any unlawful purpose or in violation of applicable law</li>
          </ul>
        </Section>

        <Section id="term-section-7" title="Intellectual Property">
          <p>
            The OGM name, logo, website design, and platform software are the
            property of One Global Marketplace Pvt. Ltd. Property images and
            descriptions remain the property of the seller, developer, or
            broker who submitted them.
          </p>
        </Section>

        <Section id="term-section-8" title="Third-Party Services">
          <p>
            The Service integrates third-party providers — including Google
            Maps, WhatsApp Business, phone/email verification, and video-call
            infrastructure for live property tours. Your use of those
            integrations is also subject to the respective third party's own
            terms and privacy policy.
          </p>
        </Section>

        <Section id="term-section-9" title="Limitation of Liability">
          <p>
            To the maximum extent permitted by law, OGM is not liable for any
            direct, indirect, incidental, or consequential damages arising from
            Your use of the Service, including any transaction, dispute, or
            miscommunication between users, or reliance on AI-generated
            responses.
          </p>
        </Section>

        <Section id="term-section-10" title="Termination">
          <p>
            We may suspend or terminate Your access to the Service at Our
            discretion, without notice, if We reasonably believe You have
            violated these Terms or applicable law.
          </p>
        </Section>

        <Section id="term-section-11" title="Governing Law">
          <p>
            These Terms are governed by the laws of India, and the courts of
            Bengaluru, Karnataka shall have exclusive jurisdiction over any
            dispute arising from Your use of the Service.
          </p>
        </Section>

        <Section id="term-section-12" title="Changes to These Terms">
          <p>
            We may update these Terms from time to time. Material changes will
            be reflected by updating the "Last updated" date above. Continued
            use of the Service after a change constitutes acceptance of the
            revised Terms.
          </p>
        </Section>

        <div className="pp-contact" id="term-section-13">
          <div className="pp-contact-icon">✉</div>
          <h2 className="pp-h2" style={{ marginBottom: 8 }}>Contact Us</h2>
          <p style={{ margin: "0 0 16px" }}>
            Questions about these Terms? Reach out any time:
          </p>
          <Link to="/contact" className="pp-contact-link">
            Visit our Contact Page →
          </Link>
        </div>

      </div>
    </div>
  );
}
